import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Icons } from '../common';
import './AdminLogin.css';
import { API_BASE } from '../../constants/urls';

const ResetPassword = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [formData, setFormData] = useState({
        email: location.state?.email || '',
        code: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.newPassword !== formData.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (formData.newPassword.length < 6) {
            setError('Password must be at least 6 characters long');
            return;
        }

        setIsLoading(true);
        setError('');

        try {
            const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: formData.email,
                    code: formData.code,
                    newPassword: formData.newPassword
                }),
            });

            const result = await response.json();

            if (response.ok) {
                setMessage(result.message || 'Password successfully reset!');
                setTimeout(() => {
                    navigate('/admin/login');
                }, 3000);
            } else {
                setError(result.error || 'Failed to reset password');
            }
        } catch (err) {
            setError('Network error. Please try again.');
            console.error('Reset password error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="admin-login-container">
            <div className="admin-login-card">
                <div className="admin-login-header">
                    <div className="admin-login-icon">
                        <Icons.KeyIcon className="w-8 h-8 text-blue-600" />
                    </div>
                    <h1 className="admin-login-title">Reset Password</h1>
                    <p className="admin-login-subtitle">
                        Enter the 6-digit code sent to your email
                    </p>
                </div>

                {message ? (
                    <div className="animate-fadeIn">
                        <div className="success-message">
                            <Icons.CheckCircleIcon className="success-icon" />
                            {message}
                        </div>
                        <p className="text-center text-sm text-gray-600 mt-4">
                            Redirecting to login page...
                        </p>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="admin-login-form">
                        <div className="form-group" style={{ marginBottom: '1rem' }}>
                            <label htmlFor="email" className="form-label">
                                Email Address
                            </label>
                            <div className="input-wrapper">
                                <Icons.UserIcon className="input-icon" />
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="Enter your email"
                                    required
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: '1rem' }}>
                            <label htmlFor="code" className="form-label">
                                Reset Code
                            </label>
                            <div className="input-wrapper">
                                <Icons.DocumentIcon className="input-icon" />
                                <input
                                    type="text"
                                    id="code"
                                    name="code"
                                    value={formData.code}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="6-digit OTP"
                                    maxLength="6"
                                    required
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: '1rem' }}>
                            <label htmlFor="newPassword" className="form-label">
                                New Password
                            </label>
                            <div className="input-wrapper">
                                <Icons.LockIcon className="input-icon" />
                                <input
                                    type="password"
                                    id="newPassword"
                                    name="newPassword"
                                    value={formData.newPassword}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="At least 6 characters"
                                    required
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                            <label htmlFor="confirmPassword" className="form-label">
                                Confirm Password
                            </label>
                            <div className="input-wrapper">
                                <Icons.LockIcon className="input-icon" />
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    name="confirmPassword"
                                    value={formData.confirmPassword}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="Repeat new password"
                                    required
                                    disabled={isLoading}
                                />
                            </div>
                        </div>

                        {error && (
                            <div className="error-message">
                                <Icons.ExclamationIcon className="error-icon" />
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading || !formData.code || !formData.newPassword}
                            className="login-button"
                        >
                            {isLoading ? (
                                <>
                                    <div className="loading-spinner"></div>
                                    Resetting...
                                </>
                            ) : (
                                'Reset Password'
                            )}
                        </button>

                        <div className="forgot-password-link-container">
                            <button
                                type="button"
                                onClick={() => navigate('/admin/login')}
                                className="forgot-password-link"
                            >
                                Back to Login
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default ResetPassword;
